import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { AnimatedSection } from '@/components/shared/AnimatedSection'
import { ROOMS_ROUTE, CONTACT, SITE_NAME } from '@/lib/constants'
import { getImageUrl } from '@/lib/image-url'

const ACTIVITIES = [
  {
    id: 'catedral',
    titleEs: 'Visita a la Catedral de Comayagua',
    titleEn: 'Visit to the Comayagua Cathedral',
    descEs:
      'El símbolo más emblemático de la ciudad, con arquitectura colonial y el reloj más antiguo de América en funcionamiento, fabricado alrededor del siglo XII. A solo 5 minutos caminando desde el hotel.',
    descEn:
      'The most iconic symbol of the city, with colonial architecture and the oldest working clock in the Americas, made around the 12th century. Just 5 minutes walking from the hotel.',
    distanceEs: '5 min caminando',
    distanceEn: '5 min walking',
    image: '/images/nuva/gallery/activity-catedral-comayagua.jpg',
    altEs: 'Catedral de Comayagua',
    altEn: 'Comayagua Cathedral',
  },
  {
    id: 'museo',
    titleEs: 'Recorrido por el Museo Arqueológico',
    titleEn: 'Tour of the Archaeological Museum',
    descEs:
      'Descubra la rica historia precolombina de Honduras a través de artefactos mayas y piezas arqueológicas locales. El museo se encuentra a 8 minutos caminando del hotel y ofrece una visión única del pasado indígena de la región.',
    descEn:
      'Discover the rich pre-Columbian history of Honduras through Maya artifacts and local archaeological pieces. The museum is 8 minutes walking from the hotel and offers a unique view of the region\'s indigenous past.',
    distanceEs: '8 min caminando',
    distanceEn: '8 min walking',
    image: '/images/nuva/gallery/activity-museo-arqueologico.jpg',
    altEs: 'Museo Arqueológico de Comayagua',
    altEn: 'Comayagua Archaeological Museum',
  },
  {
    id: 'plaza',
    titleEs: 'Paseo por el Parque Central',
    titleEn: 'Stroll through Central Park',
    descEs:
      'La plaza principal rodeada de edificios coloniales, fuentes y jardines. El lugar perfecto para disfrutar del ambiente local tras una jornada de trabajo. A solo 5 minutos caminando desde el hotel.',
    descEn:
      'The main plaza surrounded by colonial buildings, fountains, and gardens. The perfect place to enjoy the local atmosphere after a day of work. Just 5 minutes walking from the hotel.',
    distanceEs: '5 min caminando',
    distanceEn: '5 min walking',
    image: '/images/nuva/gallery/activity-plaza-central.jpg',
    altEs: 'Parque Central de Comayagua',
    altEn: 'Comayagua Central Park',
  },
  {
    id: 'montanas',
    titleEs: 'Excursión a las Montañas de Comayagua',
    titleEn: 'Mountain Excursion around Comayagua',
    descEs:
      'Las montañas cercanas ofrecen senderismo, observación de aves y vistas panorámicas. A 20 minutos en vehículo desde el hotel, son el escape perfecto para quienes desean combinar trabajo con aventura.',
    descEn:
      'The nearby mountains offer hiking, bird-watching, and panoramic views. 20 minutes by vehicle from the hotel, they are the perfect escape for those who want to combine work with adventure.',
    distanceEs: '20 min en vehículo',
    distanceEn: '20 min by vehicle',
    image: '/images/nuva/gallery/activity-montanas-comayagua.jpg',
    altEs: 'Montañas de Comayagua',
    altEn: 'Mountains of Comayagua',
  },
]

export function ExplorePage() {
  const { i18n } = useTranslation()
  const es = !i18n.language.startsWith('en')

  return (
    <>
      <Helmet>
        <title>{`${es ? 'Actividades en Comayagua' : 'Activities in Comayagua'} | ${SITE_NAME}`}</title>
        <meta
          name="description"
          content={
            es
              ? 'Descubre los tesoros de Comayagua: Catedral colonial, Museo Arqueológico, Parque Central y excursiones a las montañas. Todo a minutos del Hotel Boutique Nueva Valladolid.'
              : 'Discover the treasures of Comayagua: colonial Cathedral, Archaeological Museum, Central Park, and mountain excursions. All minutes from Hotel Boutique Nueva Valladolid.'
          }
        />
      </Helmet>

      {/* ── HERO PARALLAX with Catedral BG ── */}
      <section
        className="relative flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `url(${getImageUrl('/images/nuva/exterior/exterior-catedral-comayagua-bg.jpg')})`,
          backgroundAttachment: 'fixed',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          paddingTop: '18rem',
          paddingBottom: '10rem',
        }}
      >
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 px-4 text-center">
          <h1
            className="text-6xl font-normal text-white"
            style={{ fontFamily: 'VeniceBridge, serif', textShadow: '0 0 15px rgba(0,0,0,0.8)' }}
          >
            {es ? 'Actividades' : 'Activities'}
          </h1>
          <h2
            className="mt-0 text-3xl font-bold uppercase tracking-wide text-[#F3EADB]"
            style={{ marginTop: '-8px' }}
          >
            {es ? 'Descubre Comayagua' : 'Discover Comayagua'}
          </h2>
        </div>
      </section>

      {/* ── INTRO TEXT ── */}
      <section className="bg-white py-24 px-10">
        <div className="mx-auto max-w-7xl">
          <AnimatedSection>
            <div className="text-center mb-2">
              <h2 className="text-3xl font-bold uppercase tracking-wide text-[#0F2966] mb-6">
                {es ? 'Descubre Comayagua' : 'Discover Comayagua'}
              </h2>
              <p className="text-base text-[#666666] leading-relaxed max-w-2xl mx-auto mb-4">
                {es
                  ? 'Aprovecha tu estancia para explorar el encanto colonial de Comayagua y disfrutar de actividades culturales, históricas y de aventura.'
                  : 'Take advantage of your stay to explore the colonial charm of Comayagua and enjoy cultural, historical, and adventure activities.'}
              </p>
              <p className="text-base text-[#666666] leading-relaxed max-w-2xl mx-auto">
                {es
                  ? 'Comayagua no solo es un destino para los negocios, sino también un lugar lleno de historia, cultura y naturaleza que invita a ser explorado. Fundada en 1537, es la ciudad colonial mejor preservada de Honduras.'
                  : 'Comayagua is not only a business destination but also a place full of history, culture, and nature that invites exploration. Founded in 1537, it is the best-preserved colonial city in Honduras.'}
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ── ACTIVITY CARDS — alternating white / navy ── */}
      {ACTIVITIES.map((activity, i) => {
        const isDark = i % 2 !== 0
        const bg = isDark ? '#0F2966' : '#FFFFFF'
        const headingColor = isDark ? '#FFFFFF' : '#000000'
        const textColor = isDark ? 'rgba(255,255,255,0.85)' : '#666666'
        const labelColor = isDark ? '#F3EADB' : '#0F2966'

        return (
          <section
            key={activity.id}
            style={{ backgroundColor: bg, paddingTop: '50px', paddingBottom: '15px', paddingLeft: '80px', paddingRight: '80px' }}
          >
            <div className="mx-auto max-w-7xl">
              <AnimatedSection delay={0.05}>
                <div className="grid grid-cols-1 gap-12 md:grid-cols-12 items-start">
                  {/* Image column — arch shape */}
                  <div className="md:col-span-4">
                    <div
                      style={{
                        backgroundImage: `url(${getImageUrl(activity.image)})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        borderRadius: '300px 300px 0 0',
                        height: '380px',
                        width: '100%',
                      }}
                      role="img"
                      aria-label={es ? activity.altEs : activity.altEn}
                    />
                  </div>

                  {/* Text column */}
                  <div className="md:col-span-8 pt-5 pb-5 pl-8">
                    <h5
                      className="text-base font-bold uppercase tracking-widest mb-3"
                      style={{ color: labelColor }}
                    >
                      {es ? activity.distanceEs : activity.distanceEn}
                    </h5>
                    <h2
                      className="text-3xl font-bold uppercase tracking-wide mb-5"
                      style={{ color: headingColor }}
                    >
                      {es ? activity.titleEs : activity.titleEn}
                    </h2>
                    <p
                      className="text-base leading-relaxed max-w-xl"
                      style={{ color: textColor }}
                    >
                      {es ? activity.descEs : activity.descEn}
                    </p>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </section>
        )
      })}

      {/* ── PARALLAX CTA — Discover Activities ── */}
      <section
        className="relative flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `url(${getImageUrl('/images/nuva/exterior/exterior-cerro-azul-meambar.jpg')})`,
          backgroundAttachment: 'fixed',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          paddingTop: '12rem',
          paddingBottom: '12rem',
        }}
      >
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 px-4 text-center max-w-3xl mx-auto">
          <h2
            className="text-5xl font-normal text-white mb-6"
            style={{ fontFamily: 'VeniceBridge, serif' }}
          >
            {es ? 'Hotel Boutique Nueva Valladolid' : 'Hotel Boutique Nueva Valladolid'}
          </h2>
          <p className="text-base text-white/90 leading-relaxed mb-10">
            {es
              ? 'Reserve su estancia y descubra todo lo que Comayagua tiene para ofrecerle. Historia, cultura, naturaleza y aventura le esperan a pasos del hotel.'
              : 'Book your stay and discover everything Comayagua has to offer. History, culture, nature, and adventure await you steps from the hotel.'}
          </p>
          <Link
            to={ROOMS_ROUTE}
            className="inline-flex items-center justify-center rounded-full border border-white bg-transparent text-white text-sm font-bold uppercase tracking-wide hover:bg-[#F3EADB] hover:text-[#0F2966] transition-colors"
            style={{ width: '200px', height: '50px' }}
          >
            {es ? 'Reservar Ahora' : 'Book Now'}
          </Link>
        </div>
      </section>

      {/* ── SUGGESTED ITINERARIES ── */}
      <section className="bg-white py-24 px-10">
        <div className="mx-auto max-w-7xl">
          <AnimatedSection>
            <h2 className="text-center text-3xl font-bold uppercase tracking-wide text-[#0F2966] mb-16">
              {es ? 'Itinerarios Sugeridos' : 'Suggested Itineraries'}
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {[
              {
                labelEs: 'Medio Día',
                labelEn: 'Half Day',
                titleEs: 'Historia Colonial',
                titleEn: 'Colonial History',
                stepsEs: [
                  'Catedral de Comayagua (5 min)',
                  'Parque Central (5 min)',
                  'Museo Arqueológico (8 min)',
                  'Regreso al hotel a pie',
                ],
                stepsEn: [
                  'Comayagua Cathedral (5 min)',
                  'Central Park (5 min)',
                  'Archaeological Museum (8 min)',
                  'Walk back to the hotel',
                ],
              },
              {
                labelEs: 'Día Completo',
                labelEn: 'Full Day',
                titleEs: 'Naturaleza y Cultura',
                titleEn: 'Nature & Culture',
                stepsEs: [
                  'Desayuno en el hotel',
                  'Montañas de Comayagua (20 min)',
                  'Senderismo y vistas panorámicas',
                  'Visita al centro histórico',
                  'Cena en el hotel',
                ],
                stepsEn: [
                  'Breakfast at the hotel',
                  'Comayagua mountains (20 min)',
                  'Hiking and panoramic views',
                  'Visit to the historic center',
                  'Dinner at the hotel',
                ],
              },
              {
                labelEs: 'Excursión',
                labelEn: 'Day Trip',
                titleEs: 'Cuevas y Lago',
                titleEn: 'Caves & Lake',
                stepsEs: [
                  'Salida temprana del hotel',
                  'Cuevas de Taulabé',
                  'Lago de Yojoa',
                  'Cascada de Pulhapanzak',
                  'Regreso al hotel al atardecer',
                ],
                stepsEn: [
                  'Early departure from hotel',
                  'Taulabé Caves',
                  'Lake Yojoa',
                  'Pulhapanzak Waterfall',
                  'Return to hotel at sunset',
                ],
              },
            ].map((itinerary, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="border border-[#EEE7E0] p-8 h-full" style={{ borderRadius: '300px 300px 0 0' }}>
                  <p className="text-xs font-bold uppercase tracking-widest text-[#61988E] mb-2 text-center pt-8">
                    {es ? itinerary.labelEs : itinerary.labelEn}
                  </p>
                  <h3 className="text-2xl font-bold uppercase tracking-wide text-[#0F2966] mb-6 text-center">
                    {es ? itinerary.titleEs : itinerary.titleEn}
                  </h3>
                  <ul className="space-y-3">
                    {(es ? itinerary.stepsEs : itinerary.stepsEn).map((step, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <span className="mt-1.5 flex-shrink-0 w-2 h-2 rounded-full bg-[#0F2966]" />
                        <span className="text-sm text-[#666666]">{step}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── MAP ── */}
      <section className="bg-[#F3EADB] py-16 px-10">
        <div className="mx-auto max-w-7xl">
          <AnimatedSection>
            <h2 className="text-center text-3xl font-bold uppercase tracking-wide text-[#0F2966] mb-10">
              {es ? 'Cómo llegar' : 'How to Get Here'}
            </h2>
            <div className="overflow-hidden shadow-sm" style={{ borderRadius: '0' }}>
              <iframe
                src={CONTACT.googleMapsEmbed}
                width="100%"
                height="450"
                style={{ border: 0, display: 'block' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={es ? 'Mapa Hotel Boutique Nueva Valladolid, Comayagua' : 'Map Hotel Boutique Nueva Valladolid, Comayagua'}
              />
            </div>
            <p className="mt-6 text-center text-sm text-[#666666]">
              {es
                ? 'Hotel Boutique Nueva Valladolid — Barrio Abajo, Comayagua, 12101, Honduras'
                : 'Hotel Boutique Nueva Valladolid — Barrio Abajo, Comayagua, 12101, Honduras'}
            </p>
          </AnimatedSection>
        </div>
      </section>
    </>
  )
}
